/* eslint-disable camelcase */
import dJSON from 'dirty-json';
import Splide from '@splidejs/splide';

const moduleSelector = '.carousel-content';

/**
 * Module Init – product cards in Splide.
 */
(() => {
  // ---------------------------------------------------------------------------
  // Carousel (Splide arrows & slider)
  // ---------------------------------------------------------------------------

  const splideArrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M28.061 17.0612L19.061 26.0612C18.7792 26.343 18.397 26.5013 17.9985 26.5013C17.6 26.5013 17.2178 26.343 16.936 26.0612C16.6542 25.7794 16.4959 25.3972 16.4959 24.9987C16.4959 24.6002 16.6542 24.218 16.936 23.9362L23.3748 17.5H4.99976C4.60193 17.5 4.2204 17.3419 3.9391 17.0606C3.65779 16.7793 3.49976 16.3978 3.49976 16C3.49976 15.6022 3.65779 15.2206 3.9391 14.9393C4.2204 14.658 4.60193 14.5 4.99976 14.5H23.3748L16.9385 8.05998C16.6567 7.77818 16.4984 7.39599 16.4984 6.99747C16.4984 6.59896 16.6567 6.21677 16.9385 5.93497C17.2203 5.65318 17.6025 5.49487 18.001 5.49487C18.3995 5.49487 18.7817 5.65318 19.0635 5.93497L28.0635 14.935C28.2034 15.0745 28.3143 15.2403 28.3899 15.4228C28.4655 15.6054 28.5043 15.801 28.5041 15.9986C28.5038 16.1962 28.4646 16.3918 28.3885 16.5741C28.3125 16.7565 28.2012 16.922 28.061 17.0612Z" fill="currentColor"/></svg>';
  const splideArrowSvgPrev = splideArrowSvg.replace('<svg ', '<svg style="transform:rotate(180deg)" ');

  const repositionSplideNav = (sliderEl) => {
    const nav = sliderEl.querySelector('.splide__arrows');
    const pagination = sliderEl.querySelector('.splide__pagination');
    const prevBtn = sliderEl.querySelector('.splide__arrow--prev');
    const nextBtn = sliderEl.querySelector('.splide__arrow--next');

    if (!nav) return;

    if (pagination && nextBtn) nav.insertBefore(pagination, nextBtn);
    if (prevBtn) prevBtn.innerHTML = splideArrowSvgPrev;
    if (nextBtn) nextBtn.innerHTML = splideArrowSvg;
  };

  const getMediaSrc = (media) => media?.url ?? (typeof media === 'string' ? media : '');

  const getSlidesData = (parsed) => {
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.data)) return parsed.data;
    return [];
  };

  const getItemName = (item) => item.item_name
    ?? item.itemName
    ?? item.product_name
    ?? item['product name']
    ?? item.title
    ?? item.name
    ?? '';

  const getItemDescription = (item) => item.item_description
    ?? item.itemDescription
    ?? item.text
    ?? item.description
    ?? '';

  const getItemImage = (item) => getMediaSrc(
    item.item_image ?? item.itemImage ?? item.image ?? item.background,
  );

  const mountSplide = (sliderEl, slideCount) => {
    const hasMultiple = slideCount > 1;

    const splide = new Splide(sliderEl, {
      type: 'loop',
      rewind: true,
      fixedWidth: 'calc(100% / 3  - 96px)',
      perMove: 1,
      focus: 'center',
      gap: 24,
      trimSpace: false,
      arrows: hasMultiple,
      pagination: hasMultiple,
      drag: hasMultiple,
      speed: 400,
      breakpoints: {
        1280: {
          fixedWidth: 'calc(100% / 2 - 96px)',
        },
        900: {
          fixedWidth: 'calc(100% - 192px)',
          gap: 24,
        },
        480: {
          fixedWidth: false,
          perPage: 1,
          gap: 24,
        },
      },
    });

    splide.on('resize updated', () => {
      requestAnimationFrame(() => repositionSplideNav(sliderEl));
    });

    splide.mount();
    repositionSplideNav(sliderEl);

    return splide;
  };

  // ---------------------------------------------------------------------------
  // Contact form (quote button → scroll & pre-fill)
  // ---------------------------------------------------------------------------

  const CONTACT_FORM_SELECTOR = '.c2a-contact.quote-contact';
  const CONTACT_FORM_ID = 'contact-form';
  const SCROLL_FILL_DELAY = 700;
  const FILL_RETRY_DELAYS = [100, 300, 600];

  const getContactSection = () => document.querySelector(CONTACT_FORM_SELECTOR);
  if (!getContactSection()) return;

  const getContactMessageField = () => {
    const section = getContactSection();
    if (!section) return null;

    return section.querySelector('#message-text')
      || section.querySelector('textarea[name="message-text"]');
  };

  const getItemNameFromCard = (btn) => btn.closest('.carousel-content__card')
    ?.querySelector('.carousel-content__card__name')
    ?.textContent
    ?.trim() ?? '';

  const setContactFieldValue = (field, value) => {
    if ('value' in field) {
      field.value = value;
    } else {
      field.textContent = value;
    }

    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const fillContactMessageField = (itemName, shouldFocus = false) => {
    const value = itemName.trim();
    if (!value) return false;

    if (!getContactSection()) return false;

    const messageField = getContactMessageField();
    if (!messageField) return false;

    setContactFieldValue(messageField, value);
    if (shouldFocus) messageField.focus();
    return true;
  };

  const fillContactMessageFieldWithRetry = (itemName, shouldFocus = false) => {
    if (fillContactMessageField(itemName, shouldFocus)) return;

    FILL_RETRY_DELAYS.forEach((delay) => {
      setTimeout(
        () => fillContactMessageField(itemName, shouldFocus && delay === FILL_RETRY_DELAYS[2]),
        delay,
      );
    });
  };

  const scrollToContactForm = () => {
    const section = getContactSection();
    if (!section) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = section.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const createQuoteButton = (itemName) => {
    const btn = document.createElement('a');
    btn.href = `#${CONTACT_FORM_ID}`;
    btn.className = 'btn-custom primary carousel-content__quote-btn';
    btn.textContent = 'Get A Quote';

    if (itemName.trim()) {
      btn.dataset.itemName = itemName.trim();
    }

    return btn;
  };

  const handleQuoteClick = (e, carouselSection) => {
    const btn = e.target.closest('.carousel-content__quote-btn');
    if (!btn || !carouselSection.contains(btn)) return;

    if (!getContactSection()) return;

    e.preventDefault();

    const name = btn.dataset.itemName || getItemNameFromCard(btn);
    scrollToContactForm();
    setTimeout(() => fillContactMessageFieldWithRetry(name, true), SCROLL_FILL_DELAY);
  };

  const bindQuoteClicks = (carouselSection) => {
    if (carouselSection.dataset.quoteBound) return;

    carouselSection.addEventListener('click', (e) => handleQuoteClick(e, carouselSection));
    carouselSection.dataset.quoteBound = 'true';
  };

  // ---------------------------------------------------------------------------
  // Slides
  // ---------------------------------------------------------------------------

  const buildSlideCard = (item) => {
    const name = getItemName(item);
    const description = getItemDescription(item);
    const imgSrc = getItemImage(item);

    const slide = document.createElement('li');
    slide.className = 'splide__slide';

    const card = document.createElement('article');
    card.className = 'carousel-content__card';

    if (name.trim()) {
      const heading = document.createElement('h2');
      heading.className = 'carousel-content__card__name';
      heading.textContent = name;
      card.appendChild(heading);
    }

    if (description.trim()) {
      const copy = document.createElement('p');
      copy.className = 'carousel-content__card__description';
      copy.textContent = description;
      card.appendChild(copy);
    }

    if (imgSrc) {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'carousel-content__card__image';
      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = name.trim() || '';
      imageWrap.appendChild(img);
      card.appendChild(imageWrap);
    }

    card.appendChild(createQuoteButton(name));
    slide.appendChild(card);

    return slide;
  };

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  const modules = document.querySelectorAll(moduleSelector);
  if (!modules.length) return;

  modules.forEach((section) => {
    const sliderEl = section.querySelector('.splide');
    const sliderList = section.querySelector('.splide__list');

    if (!sliderEl || !sliderList) return;

    try {
      section.querySelector('script')?.remove();

      const parsed = dJSON.parse(section.data);
      const data = getSlidesData(parsed);

      if (!data.length) return;

      section.classList.add('carousel-content--initialized');
      bindQuoteClicks(section);

      const fragment = document.createDocumentFragment();
      data.forEach((item) => {
        fragment.appendChild(buildSlideCard(item));
      });
      sliderList.appendChild(fragment);

      const slideCount = sliderList.querySelectorAll('.splide__slide').length;
      mountSplide(sliderEl, slideCount);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error: ${moduleSelector}`, error);
    }
  });

  setTimeout(() => {
    window.dispatchEvent(new Event('resize'));
  }, 500);
})();
